import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  applyPattern, blankMarks, buildPattern, chooseGrammar, guessMarks, matchRate, slotCount,
  type Segment,
} from "./addressGrammar";
import css from "./WarehouseAdminPage.module.css";

interface Props {
  /** Addresses read from the chosen zones.json, for the sample and the match rate. */
  addresses: string[];
  /** The built (or hand-written) pattern; empty string = none. */
  value: string;
  onChange: (pattern: string) => void;
  disabled?: boolean;
  /** ADR 231: the grammar this warehouse already learned, if any. */
  remembered?: string | null;
}

/** Click order when cycling a character's meaning. `ignore` closes the loop so a
 *  mis-click is one more click to undo, not a hunt for a reset. */
const CYCLE: Segment[] = ["ignore", "aisle", "space", "level"];

/**
 * Authoring UI for the address grammar (ADR 228).
 *
 * The operator marks one real address — click a character until it reads Corredor,
 * Posição or Nível — and the regex is derived from that. Two things make it safe to
 * use without understanding what a regex is: the extraction is previewed on the
 * sample, and the match rate is computed against every address in the chosen file,
 * so "97% match" or "0% match" is visible BEFORE the import runs. A 0% grammar was
 * previously indistinguishable from a correct one until the structure came out empty.
 *
 * The raw field stays, behind a disclosure — an integrator with a pattern already
 * written should not have to reverse-engineer it through the builder.
 */
export function AddressGrammarField({ addresses, value, onChange, disabled, remembered }: Props) {
  const { t } = useTranslation();
  const [sampleIdx, setSampleIdx] = useState(0);
  const [marks, setMarks] = useState<Segment[] | null>(null);
  const [raw, setRaw] = useState(false);
  // Once the operator writes the pattern by hand, the builder stops driving it —
  // otherwise the suggestion would silently overwrite what they typed.
  const [handWritten, setHandWritten] = useState(false);

  const sample = addresses[sampleIdx] ?? "";
  // The opening grammar: the warehouse's remembered convention where it still fits
  // this file, else the suggestion. The whole file goes into the suggestion — the
  // level is the field the export holds constant, and one address on its own
  // cannot show which field that is (ADR 229/231).
  const opening = useMemo(
    () => chooseGrammar(sample, addresses, remembered), [sample, addresses, remembered]);
  // An explicit edit pins the marks; otherwise they follow the sample's shape.
  const effectiveMarks = useMemo(() => {
    if (marks && marks.length === sample.length) return marks;
    return sample ? opening.marks : [];
  }, [marks, sample, opening]);

  const built = useMemo(() => buildPattern(sample, effectiveMarks), [sample, effectiveMarks]);
  const preview = built ? applyPattern(built, sample) : null;
  const rate = useMemo(
    () => (built ? matchRate(built, addresses) : { matched: 0, total: addresses.length }),
    [built, addresses],
  );
  // A grammar can match every address and still be wrong — see slotCount.
  const slots = useMemo(
    () => (built ? slotCount(built, addresses) : { matched: 0, distinct: 0 }),
    [built, addresses],
  );
  const collides = slots.matched > 0 && slots.distinct < slots.matched;

  // Publish the built pattern, including the OPENING suggestion. Without this the
  // panel would show a match rate for a grammar the import never receives — the
  // preview and the request disagreeing is precisely the silent failure this
  // component exists to remove.
  useEffect(() => {
    if (handWritten) return;
    const next = built ?? "";
    if (next !== value) onChange(next);
  }, [built, handWritten, value, onChange]);

  function cycle(i: number) {
    const next = [...effectiveMarks];
    next[i] = CYCLE[(CYCLE.indexOf(next[i]) + 1) % CYCLE.length];
    setHandWritten(false);
    setMarks(next);
    onChange(buildPattern(sample, next) ?? "");
  }

  function reset() {
    setHandWritten(false);
    setMarks(blankMarks(sample));
    onChange("");
  }

  function useGuess() {
    const g = guessMarks(sample, addresses);
    setHandWritten(false);
    setMarks(g);
    onChange(buildPattern(sample, g) ?? "");
  }

  // No file chosen yet: the builder has nothing real to work on, so offer only the
  // raw field rather than a sample the operator would have to invent.
  if (addresses.length === 0) {
    return (
      <div className={css.field}>
        <label className={css.label} htmlFor="aisle-pattern">
          {t("structure.grammar.label")}
        </label>
        <input
          id="aisle-pattern"
          className={css.input}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("structure.grammar.pickFileFirst")}
        />
        <p className={css.note}>{t("structure.grammar.pickFileFirst")}</p>
      </div>
    );
  }

  const pct = rate.total > 0 ? Math.round((rate.matched / rate.total) * 1000) / 10 : 0;
  const rateTone = rate.matched === 0 ? css.resultError
    : rate.matched < rate.total ? css.resultWarn : css.resultOk;

  return (
    <div className={css.grammar}>
      <div className={css.label}>{t("structure.grammar.label")}</div>
      <p className={css.note}>{t("structure.grammar.help")}</p>

      <div className={css.grammarSample}>
        {[...sample].map((ch, i) => (
          <button
            key={`${ch}-${i}`}
            type="button"
            className={css.grammarChar}
            data-segment={effectiveMarks[i]}
            disabled={disabled}
            onClick={() => cycle(i)}
            aria-label={`${ch} — ${t(`structure.grammar.segment.${effectiveMarks[i]}`)}`}
          >
            {ch}
          </button>
        ))}
        {addresses.length > 1 && (
          <button
            type="button"
            className={css.linkBtn}
            disabled={disabled}
            onClick={() => setSampleIdx((i) => (i + 1) % Math.min(addresses.length, 50))}
          >
            {t("structure.grammar.nextSample")}
          </button>
        )}
      </div>

      <div className={css.grammarLegend}>
        {(["aisle", "space", "level", "ignore"] as Segment[]).map((s) => (
          <span key={s} className={css.grammarLegendItem} data-segment={s}>
            {t(`structure.grammar.segment.${s}`)}
          </span>
        ))}
      </div>

      <div className={css.grammarActions}>
        <button type="button" className={css.linkBtn} disabled={disabled} onClick={useGuess}>
          {t("structure.grammar.guess")}
        </button>
        <button type="button" className={css.linkBtn} disabled={disabled} onClick={reset}>
          {t("structure.grammar.clear")}
        </button>
        <button type="button" className={css.linkBtn} onClick={() => setRaw((v) => !v)}>
          {t("structure.grammar.advanced")}
        </button>
      </div>

      {/* Once a character is clicked the grammar is the operator's, not the
          warehouse's — the notice would then be describing the wrong thing. */}
      {opening.source === "remembered" && !marks && !handWritten && (
        <p className={css.note} role="status">{t("structure.grammar.remembered")}</p>
      )}

      {preview && (
        <p className={css.note} role="status">
          {t("structure.grammar.preview", {
            aisle: preview.aisle ?? "—",
            space: preview.space ?? "—",
            level: preview.level ?? "—",
          })}
        </p>
      )}

      <p className={rateTone} role="status">
        {rate.matched === 0
          ? t("structure.grammar.rateNone", { total: rate.total })
          : t("structure.grammar.rate", { matched: rate.matched, total: rate.total, pct })}
      </p>

      {collides && (
        <p className={css.resultWarn} role="status">
          {t("structure.grammar.collide", { matched: slots.matched, distinct: slots.distinct })}
        </p>
      )}

      {raw && (
        <div className={css.field}>
          <label className={css.label} htmlFor="aisle-pattern">
            {t("structure.grammar.rawLabel")}
          </label>
          <input
            id="aisle-pattern"
            className={css.input}
            value={value}
            disabled={disabled}
            onChange={(e) => { setHandWritten(true); onChange(e.target.value); }}
            placeholder={t("structure.addresses.aislePatternPlaceholder")}
          />
        </div>
      )}
    </div>
  );
}
